import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ArrowLeft,
  Award,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Download,
  FileText,
  GraduationCap,
  History,
  Loader2,
  Mail,
  Pencil,
  Phone,
  ShieldAlert,
  User as UserIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { Drawer } from '../components/ui/Drawer';
import { SelectField, TextAreaField } from '../components/ui/Field';
import { formatDate, timeAgo } from '../lib/format';
import {
  ApplicationEditDrawer,
  ApplicationTimeline,
  daysInCurrentStage,
  DemoEvaluationDrawer,
  DemoScheduleDrawer,
  DocumentUploader,
  DEMO_DECISION_LABEL,
  DOCUMENT_KIND_LABEL,
  formatBytes,
  INTERVIEW_DECISION_LABEL,
  InterviewEvaluationDrawer,
  listAllowedNextStages,
  PROBATION_VERDICT_LABEL,
  PROBATION_VERDICT_TONE,
  SLOT_MODE_LABEL,
  STAGE_LABEL,
  STAGE_TONE_CLASSES,
  StageActionButton,
  StageChip,
  type ApplicationDetailResponse,
  type CandidateApplication,
  type DemoEvaluationRow,
  type PipelineStage,
  type ProbationSummaryEmbed,
  type PromotedTeacherEmbed,
} from '../components/hiring';

export const Route = createFileRoute('/_authed/hr/applications/$applicationId')({
  component: ApplicationDetailPage,
});

type Tab =
  | 'profile'
  | 'documents'
  | 'pipeline'
  | 'assessments'
  | 'interview'
  | 'demos'
  | 'onboarding';

const BASE_TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof FileText }> = [
  { value: 'profile', label: 'Profile', icon: UserIcon },
  { value: 'documents', label: 'Documents', icon: FileText },
  { value: 'pipeline', label: 'Pipeline', icon: History },
  { value: 'assessments', label: 'Tests', icon: ClipboardCheck },
  { value: 'interview', label: 'Interview', icon: Mail },
  { value: 'demos', label: 'Demos', icon: Award },
];

const ONBOARDING_TAB: { value: Tab; label: string; icon: typeof FileText } = {
  value: 'onboarding',
  label: 'Onboarding',
  icon: GraduationCap,
};

const HR_ROLES = ['super_admin', 'academic_admin', 'hr'] as const;

/**
 * Pipeline stages that surface the Onboarding tab. The tab is informational
 * once the candidate either passed rules or completed onboarding — both
 * surfaces show the promoted-teacher + probation cards, with the rules
 * surface explaining the next promotion step when no teacher exists yet.
 */
const ONBOARDING_STAGES: ReadonlySet<PipelineStage> = new Set<PipelineStage>([
  'rules_passed',
  'onboarded',
]);

/**
 * Stages at which HR can promote a candidate into a teacher record.
 * Server gate is super_admin only; UI mirrors that to avoid presenting
 * a button that will 403.
 */
const PROMOTE_STAGES: ReadonlySet<PipelineStage> = new Set<PipelineStage>([
  'selected',
  'rules_passed',
]);

function ApplicationDetailPage() {
  const { applicationId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole);
  const isHr = role ? (HR_ROLES as readonly string[]).includes(role) : false;
  const isSuperAdmin = role === 'super_admin';

  const [tab, setTab] = useState<Tab>('profile');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [interviewDrawer, setInterviewDrawer] = useState<{
    bookingId: string;
    initial: Partial<import('@bbl/validation').InterviewEvaluationInput> | null;
  } | null>(null);
  const [demoDrawer, setDemoDrawer] = useState<{
    demoId: string;
    initial: Partial<import('@bbl/validation').DemoEvaluationInput> | null;
  } | null>(null);

  const detailQuery = useQuery({
    queryKey: ['hiring', 'application', applicationId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ApplicationDetailResponse>(
        `/api/hiring/applications/${encodeURIComponent(applicationId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['hiring', 'application', applicationId],
    });
    void queryClient.invalidateQueries({ queryKey: ['hiring', 'applications'] });
  };

  const issueAptitudeMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ runId: string }>(
        `/api/hiring/applications/${encodeURIComponent(applicationId)}/aptitude/issue`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      toast('Aptitude test issued. Candidate notified by email.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not issue test.', { variant: 'error' }),
  });

  const issueRulesMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ runId: string }>(
        `/api/hiring/applications/${encodeURIComponent(applicationId)}/rules/issue`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      toast('Rules test issued.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not issue rules test.', { variant: 'error' }),
  });

  const archiveMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/hiring/applications/${encodeURIComponent(applicationId)}/archive`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      toast('Application archived.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not archive.', { variant: 'error' }),
  });

  const transitionMutation = useMutation({
    mutationFn: (body: { toStage: PipelineStage; reason?: string }) =>
      apiRequest<{ ok: true }>(
        `/api/hiring/applications/${encodeURIComponent(applicationId)}/transition`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      toast('Pipeline updated.', { variant: 'success' });
      setOverrideOpen(false);
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Transition rejected.', { variant: 'error' }),
  });

  const promoteMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true; teacherId: string }>(
        `/api/hiring/applications/${encodeURIComponent(applicationId)}/promote`,
        { method: 'POST', body: {} },
      ),
    onSuccess: (res) => {
      toast(`Candidate promoted to teacher (${res.teacherId.slice(0, 8)}).`, {
        variant: 'success',
      });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not promote candidate.', {
        variant: 'error',
      }),
  });

  const handlePromote = async () => {
    const ok = await confirm({
      title: 'Promote to teacher?',
      description:
        'A teacher record is created from this application and the candidate enters probation. This cannot be undone from the portal.',
      confirmLabel: 'Promote',
    });
    if (!ok) return;
    promoteMutation.mutate();
  };

  const data = detailQuery.data;
  const application = data?.application;

  const activeBookingId = useMemo(() => {
    if (!data) return null;
    const open = data.bookings.find((b) => b.status === 'booked' || b.status === 'attended');
    return open?.id ?? null;
  }, [data]);

  const daysInStage = useMemo(() => {
    if (!data) return 0;
    return daysInCurrentStage(data.application, data.statusTimeline);
  }, [data]);

  const handleArchive = async () => {
    const ok = await confirm({
      title: 'Archive this application?',
      description:
        'The candidate keeps a read-only view of their dashboard; HR removes it from active queues.',
      confirmLabel: 'Archive',
      variant: 'danger',
    });
    if (!ok) return;
    archiveMutation.mutate();
  };

  return (
    <ListShell
      title={application?.fullName ?? 'Application'}
      description={
        application
          ? `Submitted ${formatDate(application.submittedAt, { withTime: true })}`
          : undefined
      }
      primaryAction={
        <Link
          to="/hr/applications"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to applications
        </Link>
      }
    >
      <GatedSection
        error={detailQuery.error}
        title="Application"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !data || !application ? (
          <div className="space-y-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <SummaryHeader
              application={application}
              promotedTeacherId={data.promotedTeacher?.id ?? null}
              daysInStage={daysInStage}
              activeBookingId={activeBookingId}
              demos={data.demos}
              canAct={isHr}
              canEdit={isHr}
              canOverride={isSuperAdmin}
              canPromote={
                isSuperAdmin && PROMOTE_STAGES.has(application.pipelineStage)
              }
              issuing={issueAptitudeMutation.isPending || issueRulesMutation.isPending}
              archiving={archiveMutation.isPending}
              promoting={promoteMutation.isPending}
              onEdit={() => setEditOpen(true)}
              onIssueAptitude={() => issueAptitudeMutation.mutate()}
              onIssueRules={() => issueRulesMutation.mutate()}
              onScheduleDemos={() => setScheduleOpen(true)}
              onArchive={handleArchive}
              onPromote={() => {
                void handlePromote();
              }}
              onViewProbation={(teacherId) => {
                // StageActionButton hides this affordance entirely when
                // `teacherId` is null, so we can navigate unconditionally
                // when invoked. The teacherId guard remains for defensive
                // typing — the action signature still allows null.
                if (!teacherId) return;
                void navigate({
                  to: '/hr/probation/$teacherId',
                  params: { teacherId },
                });
              }}
              onOverride={() => setOverrideOpen(true)}
            />

            <div
              role="tablist"
              aria-label="Application sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {(ONBOARDING_STAGES.has(application.pipelineStage)
                ? [...BASE_TABS, ONBOARDING_TAB]
                : BASE_TABS
              ).map((opt) => {
                const Icon = opt.icon;
                const active = tab === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(opt.value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {tab === 'profile' ? <ProfileTab data={data} /> : null}
            {tab === 'documents' ? (
              <DocumentsTab
                applicationId={applicationId}
                documents={data.documents}
                onChanged={invalidate}
              />
            ) : null}
            {tab === 'pipeline' ? (
              <PipelineTab events={data.statusTimeline} />
            ) : null}
            {tab === 'assessments' ? (
              <AssessmentsTab
                applicationId={applicationId}
                currentAttemptId={data.currentAssessmentAttemptId}
                events={data.statusTimeline}
              />
            ) : null}
            {tab === 'interview' ? (
              <InterviewTab
                bookings={data.bookings}
                onEvaluate={(bookingId, initial) =>
                  setInterviewDrawer({ bookingId, initial })
                }
                canAct={isHr}
              />
            ) : null}
            {tab === 'demos' ? (
              <DemosTab
                demos={data.demos}
                canAct={isHr}
                onEvaluate={(demoId, initial) =>
                  setDemoDrawer({ demoId, initial })
                }
              />
            ) : null}
            {tab === 'onboarding' ? (
              <OnboardingTab
                application={application}
                promotedTeacher={data.promotedTeacher ?? null}
                probationSummary={data.probationSummary ?? null}
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      <DemoScheduleDrawer
        open={scheduleOpen}
        applicationId={applicationId}
        onClose={() => setScheduleOpen(false)}
        onSaved={() => {
          setScheduleOpen(false);
          invalidate();
        }}
      />

      <InterviewEvaluationDrawer
        open={interviewDrawer !== null}
        bookingId={interviewDrawer?.bookingId ?? null}
        initial={interviewDrawer?.initial ?? null}
        onClose={() => setInterviewDrawer(null)}
        onSaved={() => {
          setInterviewDrawer(null);
          invalidate();
        }}
      />

      <DemoEvaluationDrawer
        open={demoDrawer !== null}
        demoId={demoDrawer?.demoId ?? null}
        initial={demoDrawer?.initial ?? null}
        onClose={() => setDemoDrawer(null)}
        onSaved={() => {
          setDemoDrawer(null);
          invalidate();
        }}
      />

      <ManualOverrideDrawer
        open={overrideOpen}
        currentStage={application?.pipelineStage ?? null}
        onClose={() => setOverrideOpen(false)}
        onSubmit={(toStage, reason) =>
          transitionMutation.mutate(reason ? { toStage, reason } : { toStage })
        }
        pending={transitionMutation.isPending}
      />

      <ApplicationEditDrawer
        open={editOpen}
        application={application ?? null}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          invalidate();
        }}
      />
    </ListShell>
  );
}

// ---------------------------------------------------------------------------
// Summary header
// ---------------------------------------------------------------------------

interface SummaryHeaderProps {
  application: ApplicationDetailResponse['application'];
  promotedTeacherId: string | null;
  daysInStage: number;
  activeBookingId: string | null;
  demos: ReadonlyArray<DemoEvaluationRow>;
  canAct: boolean;
  canEdit: boolean;
  canOverride: boolean;
  canPromote: boolean;
  issuing: boolean;
  archiving: boolean;
  promoting: boolean;
  onEdit: () => void;
  onIssueAptitude: () => void;
  onIssueRules: () => void;
  onScheduleDemos: () => void;
  onArchive: () => void;
  onPromote: () => void;
  onViewProbation: (teacherId: string | null) => void;
  onOverride: () => void;
}

function SummaryHeader({
  application,
  promotedTeacherId,
  daysInStage,
  activeBookingId,
  demos,
  canAct,
  canEdit,
  canOverride,
  canPromote,
  issuing,
  archiving,
  promoting,
  onEdit,
  onIssueAptitude,
  onIssueRules,
  onScheduleDemos,
  onArchive,
  onPromote,
  onViewProbation,
  onOverride,
}: SummaryHeaderProps) {
  const passedDemos = demos.filter((d) => d.decision === 'passed').length;
  const failedDemos = demos.filter((d) => d.decision === 'failed').length;

  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-semibold">
              {application.fullName}
            </h2>
            <StageChip stage={application.pipelineStage} size="lg" />
          </div>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <Row icon={<Mail size={12} aria-hidden="true" />}>{application.email}</Row>
            <Row icon={<Phone size={12} aria-hidden="true" />}>{application.phoneNumber}</Row>
            <Row icon={<ClipboardList size={12} aria-hidden="true" />}>
              {application.qualification}
              {application.experienceYears !== null
                ? ` · ${application.experienceYears}y experience`
                : ''}
            </Row>
            <Row icon={<History size={12} aria-hidden="true" />}>
              Stage age{' '}
              <strong
                className={
                  daysInStage >= 7
                    ? 'text-[color:var(--color-warning)]'
                    : 'text-[color:var(--color-text)]'
                }
              >
                {daysInStage}d
              </strong>{' '}
              · updated {timeAgo(application.updatedAt)}
            </Row>
          </dl>
          <div className="flex flex-wrap gap-1.5">
            {application.coursesPreferred.map((c) => (
              <span
                key={c}
                className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] uppercase tracking-wide text-[color:var(--color-text-muted)]"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 lg:min-w-[260px]">
          <Stat label="Demos" value={`${demos.length} / 3`} />
          <Stat label="Demos passed" value={`${passedDemos}`} />
          <Stat label="Demos failed" value={`${failedDemos}`} />
          <Stat label="Stage age" value={`${daysInStage}d`} />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--color-border)] pt-4">
        <StageActionButton
          stage={application.pipelineStage}
          activeBookingId={activeBookingId}
          promotedTeacherId={promotedTeacherId}
          canAct={canAct}
          isLoading={issuing || archiving}
          handlers={{
            onIssueAptitude,
            onIssueRules,
            onScheduleDemos,
            onArchive,
            onViewProbation,
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          {canPromote ? (
            <Button
              size="sm"
              icon={<CheckCircle2 size={14} aria-hidden="true" />}
              loading={promoting}
              onClick={onPromote}
            >
              Promote to teacher
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil size={14} aria-hidden="true" />}
              onClick={onEdit}
            >
              Edit
            </Button>
          ) : null}
          {canOverride ? (
            <Button
              variant="ghost"
              size="sm"
              icon={<ShieldAlert size={14} aria-hidden="true" />}
              onClick={onOverride}
              className="text-[color:var(--color-text-muted)]"
            >
              Manual override
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Row({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-[color:var(--color-text-muted)]">
      <span className="text-[color:var(--color-text-subtle)]">{icon}</span>
      <span className="truncate text-[color:var(--color-text)]">{children}</span>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-center">
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </p>
      <p className="mt-0.5 font-display text-lg font-bold text-[color:var(--color-text)]">
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile tab
// ---------------------------------------------------------------------------

function ProfileTab({ data }: { data: ApplicationDetailResponse }) {
  const a = data.application;
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <Card title="Basic details">
        <Field label="Full name">{a.fullName}</Field>
        <Field label="Email">{a.email}</Field>
        <Field label="Phone">{a.phoneNumber}</Field>
        <Field label="Age">{a.age ?? '—'}</Field>
        <Field label="Place">{a.place ?? '—'}</Field>
        <Field label="Marital status">{a.maritalStatus ?? '—'}</Field>
        <Field label="Children">
          {a.childrenCount !== null
            ? `${a.childrenCount}${a.childrenAges && a.childrenAges.length > 0 ? ` (ages ${a.childrenAges.join(', ')})` : ''}`
            : '—'}
        </Field>
      </Card>
      <Card title="Profile">
        <Field label="Qualification">{a.qualification}</Field>
        <Field label="Experience">
          {a.experienceYears !== null ? `${a.experienceYears} year(s)` : '—'}
        </Field>
        <Field label="Courses preferred">{a.coursesPreferred.join(', ') || '—'}</Field>
        <Field label="Cover note">
          <pre className="whitespace-pre-wrap font-sans text-sm">
            {a.coverNote ?? '—'}
          </pre>
        </Field>
        <Field label="Referral source">{a.referralSource ?? '—'}</Field>
      </Card>
      <Card title="Consent">
        <Field label="Terms of service">
          {a.consentTerms ? 'Accepted' : 'Not accepted'}
        </Field>
        <Field label="Data processing">
          {a.consentDataProcessing ? 'Accepted' : 'Not accepted'}
        </Field>
        <Field label="Submitted at">
          {formatDate(a.submittedAt, { withTime: true })}
        </Field>
      </Card>
    </section>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <h3 className="font-display text-base font-semibold">{title}</h3>
      <dl className="mt-3 space-y-2 text-sm">{children}</dl>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="col-span-2 text-[color:var(--color-text)]">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documents tab
// ---------------------------------------------------------------------------

interface DocumentsTabProps {
  applicationId: string;
  documents: ApplicationDetailResponse['documents'];
  onChanged: () => void;
}

function DocumentsTab({ applicationId, documents, onChanged }: DocumentsTabProps) {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownload = async (docId: string) => {
    setDownloading(docId);
    try {
      const res = await apiRequest<{
        url: string;
        expiresAt: string;
        expiresInSeconds: number;
      }>(
        `/api/hiring/applications/${encodeURIComponent(applicationId)}/documents/${encodeURIComponent(docId)}/download`,
      );
      // Open the presigned URL in a new tab. The URL is single-use and
      // expires quickly, so we don't cache it client-side.
      const win = window.open(res.url, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast('Pop-up blocked. Allow pop-ups for this site to download files.', {
          variant: 'error',
        });
      }
    } catch (err) {
      toast(
        err instanceof ApiError ? err.message : 'Could not generate download link.',
        { variant: 'error' },
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section className="space-y-4">
      <DocumentUploader
        applicationId={applicationId}
        onUploaded={onChanged}
      />
      {documents.length === 0 ? (
        <EmptyState
          icon={<FileText size={28} aria-hidden="true" />}
          title="No documents yet"
          description="Upload certificates, ID proof, transcripts, and resumes here. Files are stored privately."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-surface)]">
              <tr>
                <Th>Kind</Th>
                <Th>Filename</Th>
                <Th>Size</Th>
                <Th>Uploaded</Th>
                <Th>
                  <span className="sr-only">Download</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => {
                const pending = downloading === doc.id;
                return (
                  <tr
                    key={doc.id}
                    className="border-t border-[color:var(--color-border)]"
                  >
                    <td className="px-4 py-2">
                      <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] uppercase tracking-wide">
                        {DOCUMENT_KIND_LABEL[doc.kind] ?? doc.kind}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-[color:var(--color-text)]">
                      {doc.originalFilename}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-[color:var(--color-text-muted)]">
                      {formatBytes(doc.sizeBytes)}
                    </td>
                    <td className="px-4 py-2 text-xs text-[color:var(--color-text-muted)]">
                      {formatDate(doc.uploadedAt, { withTime: true })}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={
                          pending ? (
                            <Loader2
                              size={14}
                              aria-hidden="true"
                              className="animate-spin"
                            />
                          ) : (
                            <Download size={14} aria-hidden="true" />
                          )
                        }
                        onClick={() => {
                          void handleDownload(doc.id);
                        }}
                        disabled={pending}
                      >
                        Download
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-[color:var(--color-text-muted)]">
        Files live in R2 under <code className="font-mono">{`hiring/{applicationId}/{kind}/{id}`}</code>.
        Download links are single-use and expire in 5 minutes.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pipeline tab
// ---------------------------------------------------------------------------

function PipelineTab({ events }: { events: ApplicationDetailResponse['statusTimeline'] }) {
  return (
    <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <header className="mb-4">
        <h3 className="font-display text-base font-semibold">Status history</h3>
        <p className="text-xs text-[color:var(--color-text-muted)]">
          Every change is recorded — both automatic (test results,
          demo auto-decision) and manual.
        </p>
      </header>
      <ApplicationTimeline events={events} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Assessments tab
// ---------------------------------------------------------------------------

interface AssessmentsTabProps {
  applicationId: string;
  currentAttemptId: string | null;
  events: ApplicationDetailResponse['statusTimeline'];
}

function AssessmentsTab({
  currentAttemptId,
  events,
}: AssessmentsTabProps) {
  // Surface aptitude / rules attempts inferred from the status timeline
  // metadata. The backend writes attemptId + scorePercent into
  // candidate_status_events.metadataJson when it transitions.
  const items = useMemo(() => {
    type AssessmentItem = {
      label: 'Aptitude' | 'Rules';
      outcome: 'passed' | 'failed';
      runId: string | null;
      attemptId: string | null;
      scorePercent: number | null;
      decidedAt: string;
    };
    const out: AssessmentItem[] = [];
    for (const e of events) {
      if (!e.metadata || typeof e.metadata !== 'object') continue;
      const meta = e.metadata as Record<string, unknown>;
      if (e.toStage === 'aptitude_passed' || e.toStage === 'aptitude_failed') {
        out.push({
          label: 'Aptitude',
          outcome: e.toStage === 'aptitude_passed' ? 'passed' : 'failed',
          runId: typeof meta.runId === 'string' ? meta.runId : null,
          attemptId: typeof meta.attemptId === 'string' ? meta.attemptId : null,
          scorePercent: typeof meta.scorePercent === 'number' ? meta.scorePercent : null,
          decidedAt: e.createdAt,
        });
      }
      if (e.toStage === 'rules_passed' || e.toStage === 'rules_failed') {
        out.push({
          label: 'Rules',
          outcome: e.toStage === 'rules_passed' ? 'passed' : 'failed',
          runId: typeof meta.runId === 'string' ? meta.runId : null,
          attemptId: typeof meta.attemptId === 'string' ? meta.attemptId : null,
          scorePercent: typeof meta.scorePercent === 'number' ? meta.scorePercent : null,
          decidedAt: e.createdAt,
        });
      }
    }
    return out;
  }, [events]);

  if (items.length === 0 && !currentAttemptId) {
    return (
      <EmptyState
        icon={<ClipboardCheck size={28} aria-hidden="true" />}
        title="No tests taken yet"
        description="The candidate's attempts will appear here once they take the aptitude or rules test."
      />
    );
  }

  return (
    <section className="space-y-3">
      <ul className="space-y-2">
        {items.map((it, idx) => (
          <li
            key={`${it.label}-${idx}`}
            className="flex items-center justify-between rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 py-3"
          >
            <div>
              <p className="font-display text-sm font-semibold">
                {it.label} test
              </p>
              <p className="text-xs text-[color:var(--color-text-muted)]">
                {formatDate(it.decidedAt, { withTime: true })}
                {it.scorePercent !== null ? ` · scored ${it.scorePercent}%` : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={clsx(
                  'rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                  it.outcome === 'passed'
                    ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
                    : 'border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 text-[color:var(--color-danger)]',
                )}
              >
                {it.outcome}
              </span>
              {it.attemptId ? (
                <Link
                  to="/assessments/attempts/$attemptId/result"
                  params={{ attemptId: it.attemptId }}
                  className="text-xs text-gold-700 underline hover:no-underline"
                >
                  View attempt
                </Link>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Interview tab
// ---------------------------------------------------------------------------

interface InterviewTabProps {
  bookings: ApplicationDetailResponse['bookings'];
  canAct: boolean;
  onEvaluate: (
    bookingId: string,
    initial: Partial<import('@bbl/validation').InterviewEvaluationInput> | null,
  ) => void;
}

function InterviewTab({ bookings, canAct, onEvaluate }: InterviewTabProps) {
  if (bookings.length === 0) {
    return (
      <EmptyState
        icon={<Mail size={28} aria-hidden="true" />}
        title="No interview booked yet"
        description="The candidate books a slot from their dashboard once they pass the aptitude test."
      />
    );
  }
  return (
    <section className="space-y-3">
      {bookings.map((b) => {
        const initial = b.evaluation
          ? {
              decision: b.evaluation.decision,
              ...(b.evaluation.overallScore !== null ? { overallScore: b.evaluation.overallScore } : {}),
              ...(b.evaluation.communicationScore !== null ? { communicationScore: b.evaluation.communicationScore } : {}),
              ...(b.evaluation.subjectKnowledgeScore !== null ? { subjectKnowledgeScore: b.evaluation.subjectKnowledgeScore } : {}),
              ...(b.evaluation.pedagogyScore !== null ? { pedagogyScore: b.evaluation.pedagogyScore } : {}),
              ...(b.evaluation.professionalismScore !== null ? { professionalismScore: b.evaluation.professionalismScore } : {}),
              ...(b.evaluation.notes ? { notes: b.evaluation.notes } : {}),
            }
          : null;
        return (
          <article
            key={b.id}
            className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-display text-sm font-semibold">
                  {formatDate(b.slot.startsAt, { withTime: true })}
                </p>
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  {SLOT_MODE_LABEL[b.slot.mode]} ·{' '}
                  {b.slot.locationOrLink ?? 'No location set'}
                </p>
                <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                  Booking status:{' '}
                  <span className="font-medium text-[color:var(--color-text)]">
                    {b.status}
                  </span>
                  {b.cancelledReason ? ` · ${b.cancelledReason}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {b.evaluation ? (
                  <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] uppercase tracking-wide">
                    {INTERVIEW_DECISION_LABEL[b.evaluation.decision]}
                    {b.evaluation.overallScore !== null
                      ? ` · ${b.evaluation.overallScore}`
                      : ''}
                  </span>
                ) : null}
                {canAct ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onEvaluate(b.id, initial)}
                    disabled={b.status === 'cancelled'}
                  >
                    {b.evaluation ? 'Edit evaluation' : 'Evaluate'}
                  </Button>
                ) : null}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Demos tab
// ---------------------------------------------------------------------------

interface DemosTabProps {
  demos: ReadonlyArray<DemoEvaluationRow>;
  canAct: boolean;
  onEvaluate: (
    demoId: string,
    initial: Partial<import('@bbl/validation').DemoEvaluationInput> | null,
  ) => void;
}

function DemosTab({ demos, canAct, onEvaluate }: DemosTabProps) {
  if (demos.length === 0) {
    return (
      <EmptyState
        icon={<Award size={28} aria-hidden="true" />}
        title="No demo classes scheduled"
        description="Schedule three demos once the candidate passes the interview."
      />
    );
  }
  return (
    <section className="space-y-3">
      {demos.map((d) => {
        const initial = {
          decision: d.decision,
          ...(d.heldAt ? { heldAt: d.heldAt } : {}),
          ...(d.overallScore !== null ? { overallScore: d.overallScore } : {}),
          ...(d.engagementScore !== null ? { engagementScore: d.engagementScore } : {}),
          ...(d.clarityScore !== null ? { clarityScore: d.clarityScore } : {}),
          ...(d.classroomManagementScore !== null ? { classroomManagementScore: d.classroomManagementScore } : {}),
          ...(d.mistakesNoted ? { mistakesNoted: d.mistakesNoted } : {}),
          ...(d.strengthsNoted ? { strengthsNoted: d.strengthsNoted } : {}),
          ...(d.notes ? { notes: d.notes } : {}),
          ...(d.topic ? { topic: d.topic } : {}),
          ...(d.audience ? { audience: d.audience } : {}),
        };
        return (
          <article
            key={d.id}
            className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-display text-sm font-semibold">
                  Demo #{d.sequenceNo} · {formatDate(d.scheduledAt, { withTime: true })}
                </p>
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  Evaluator <code className="font-mono">{d.evaluatorId.slice(0, 8)}</code>
                  {d.topic ? ` · ${d.topic}` : ''}
                  {d.audience ? ` · ${d.audience}` : ''}
                </p>
                {d.overallScore !== null ? (
                  <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                    Overall <strong>{d.overallScore}</strong>
                    {d.engagementScore !== null ? ` · engagement ${d.engagementScore}` : ''}
                    {d.clarityScore !== null ? ` · clarity ${d.clarityScore}` : ''}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={clsx(
                    'rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide',
                    d.decision === 'passed'
                      ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
                      : d.decision === 'failed'
                        ? 'border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 text-[color:var(--color-danger)]'
                        : 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
                  )}
                >
                  {DEMO_DECISION_LABEL[d.decision]}
                </span>
                {canAct ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onEvaluate(d.id, initial)}
                  >
                    {d.decision === 'pending' ? 'Evaluate' : 'Edit'}
                  </Button>
                ) : null}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {children}
    </th>
  );
}

// ---------------------------------------------------------------------------
// Onboarding tab
// ---------------------------------------------------------------------------

interface OnboardingTabProps {
  application: CandidateApplication;
  promotedTeacher: PromotedTeacherEmbed | null;
  probationSummary: ProbationSummaryEmbed | null;
}

function OnboardingTab({
  application,
  promotedTeacher,
  probationSummary,
}: OnboardingTabProps) {
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <Card title="Promoted teacher">
        {promotedTeacher ? (
          <div className="space-y-3">
            <Field label="Name">{promotedTeacher.name}</Field>
            <Field label="Employee ID">
              {promotedTeacher.employeeId ?? '—'}
            </Field>
            <Field label="Joined">
              {promotedTeacher.joinedAt
                ? formatDate(promotedTeacher.joinedAt)
                : '—'}
            </Field>
            <div className="pt-2">
              <Link
                to="/people/teachers/$teacherId"
                params={{ teacherId: promotedTeacher.id }}
                className="inline-flex items-center gap-1 text-sm font-medium text-gold-700 hover:underline"
              >
                Open teacher profile
              </Link>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={<GraduationCap size={28} aria-hidden="true" />}
            title="Not promoted yet"
            description={
              application.pipelineStage === 'rules_passed'
                ? 'The candidate has passed the rules test. Use the Promote to teacher action above to create their teacher record and start probation.'
                : 'The candidate will appear here once their teacher record is created.'
            }
          />
        )}
      </Card>

      <Card title="Probation summary">
        {probationSummary && promotedTeacher ? (
          <div className="space-y-3">
            <Field label="Weeks recorded">
              {`${probationSummary.weeksRecorded} of 12`}
            </Field>
            <Field label="Latest week">
              {probationSummary.latestWeekNo
                ? `Week ${probationSummary.latestWeekNo}`
                : '—'}
            </Field>
            <Field label="Latest verdict">
              {probationSummary.latestVerdict ? (
                <span
                  className={clsx(
                    'inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                    STAGE_TONE_CLASSES[
                      PROBATION_VERDICT_TONE[probationSummary.latestVerdict]
                    ],
                  )}
                >
                  {PROBATION_VERDICT_LABEL[probationSummary.latestVerdict]}
                </span>
              ) : (
                '—'
              )}
            </Field>
            <Field label="Average score">
              {probationSummary.averageScore !== null
                ? `${Math.round(probationSummary.averageScore)} / 100`
                : '—'}
            </Field>
            <div className="pt-2">
              <Link
                to="/hr/probation/$teacherId"
                params={{ teacherId: promotedTeacher.id }}
                className="inline-flex items-center gap-1 text-sm font-medium text-gold-700 hover:underline"
              >
                Open probation reviews
              </Link>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={<ClipboardCheck size={28} aria-hidden="true" />}
            title="No probation reviews yet"
            description="Weekly reviews will appear here once the first probation review is recorded."
          />
        )}
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Manual override drawer (super_admin only)
// ---------------------------------------------------------------------------

interface ManualOverrideDrawerProps {
  open: boolean;
  currentStage: PipelineStage | null;
  onClose: () => void;
  onSubmit: (toStage: PipelineStage, reason: string) => void;
  pending: boolean;
}

function ManualOverrideDrawer({
  open,
  currentStage,
  onClose,
  onSubmit,
  pending,
}: ManualOverrideDrawerProps) {
  const [toStage, setToStage] = useState<PipelineStage | ''>('');
  const [reason, setReason] = useState('');

  const allowed = currentStage ? listAllowedNextStages(currentStage) : [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Manual pipeline override"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!toStage}
            loading={pending}
            onClick={() => {
              if (toStage) onSubmit(toStage as PipelineStage, reason);
            }}
          >
            Transition
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          The state machine permits the destinations listed below from the
          current stage. The candidate will be notified of stage changes when
          relevant.
        </p>
        <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-sm">
          <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            Current stage
          </p>
          <p className="mt-0.5 font-medium">
            {currentStage ? STAGE_LABEL[currentStage] : '—'}
          </p>
        </div>
        <SelectField
          label="Transition to"
          value={toStage}
          onChange={(e) => setToStage(e.target.value as PipelineStage | '')}
          options={[
            { value: '', label: '— Select target stage —' },
            ...allowed.map((s) => ({ value: s, label: STAGE_LABEL[s] })),
          ]}
        />
        <TextAreaField
          label="Reason"
          optional
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="A note for the activity history — the candidate and HR can see it."
        />
      </div>
    </Drawer>
  );
}
